import React from 'react';
import { Link } from 'react-router-dom';
import { AmplifySignOut } from '@aws-amplify/ui-react';
import {
  Drawer,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import GstRecordIcon from '@material-ui/icons/PostAdd';
import RoomTransferIcon from '@material-ui/icons/SwapHorizontalCircleRounded';
import RoomCleanIcon from '@mui/icons-material/CleaningServices';
import EditPackageIcon from '@material-ui/icons/RestaurantMenu';
import UpgradeIcon from '@material-ui/icons/UpdateRounded';
import NoteIcon from '@material-ui/icons/NoteTwoTone';

import CheckinIcon from '../assets/images/check-in-v2.svg';
import CheckoutIcon from '../assets/images/check-out-v2.svg';
import ExtraBedIcon from '../assets/images/extra-bed-v2.svg';
import GuestRegisterIcon from '../assets/images/guest-info-v2.svg';
import AdvanceIcon from '../assets/images/advance-payment-v2.svg';
import InvoiceIcon from '../assets/images/invoice-v2.svg';
import LaundryIcon from '../assets/images/miscellaneous-v2.svg';
import FoodBillsIcon from '../assets/images/food-bills-v2.svg';
import ReportsIcon from '../assets/images/reports-v2.svg';
import PositionListIcon from '../assets/images/position-list-v2.svg';
import RoomCleanerGridIcon from '../assets/images/room-cleaner.svg';
import AdvanceBookingsIcon from '../assets/images/advance-bookings.svg';
import RestaurantBills from '../assets/images/restaurant_bills_2.svg';

import { Card } from '../stories/Card';

interface HomeProps {
  isProduction: boolean;
  departmentName: string;
  packageFeatureEnabled: boolean;
  upgradeOccupancyFeatureEnabled: boolean;
  posInvoiceFeatureEnabled?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  drawerButton: {
    width: '40px',
    display: 'flex',
    alignSelf: 'left',
    marginLeft: '24px',
  },
  dPaper: {
    minWidth: '240px',
    top: 'initial',
  },
});

const Home = ({
  isProduction,
  departmentName,
  packageFeatureEnabled,
  upgradeOccupancyFeatureEnabled,
  posInvoiceFeatureEnabled,
}: HomeProps) => {
  const classes = useStyles();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const toggleDrawer = (open: boolean) => (event: any) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    setSidebarOpen(open);
  };

  const removeValueFromArray = (value: string, array: any[]) => {
    const index = array.findIndex((v) => {
      return v.label === value;
    });

    if (index > -1) {
      array.splice(index, 1);
    }
  };

  const list = (anchor: string) => {
    const listItems = [
      {
        label: 'GST Record',
        icon: <GstRecordIcon />,
        linkTo: '/gstRecord',
      },
      {
        label: 'Room Transfer',
        icon: <RoomTransferIcon />,
        linkTo: '/roomTransfer',
      },
      {
        label: 'Room Cleaner',
        icon: <RoomCleanIcon />,
        linkTo: '/roomCleaners',
      },
      {
        label: 'Edit Package',
        icon: <EditPackageIcon />,
        linkTo: '/editPackage',
      },
      {
        label: 'Upgrade Occupancy',
        icon: <UpgradeIcon />,
        linkTo: '/upgradeOccupancy',
      },
      {
        label: 'Add Notes',
        icon: <NoteIcon />,
        linkTo: '/CheckinNotes',
      },
    ];

    let activatedIndices: number[] = [];
    let activatedItems: { label: string; icon: JSX.Element; linkTo: string }[];

    switch (departmentName) {
      case 'Reception':
        activatedItems = [...listItems];

        if (packageFeatureEnabled === false) {
          removeValueFromArray('Edit Package', activatedItems);
        }

        if (upgradeOccupancyFeatureEnabled === false) {
          removeValueFromArray('Upgrade Occupancy', activatedItems);
        }

        activatedIndices = activatedItems.map((_, ind) => ind);
        activatedIndices.push(-1);

        break;
      case 'House Keeping':
        activatedIndices = [-1];
        break;

      case 'Accounts':
        activatedIndices = [-1];
        break;

      default:
        break;
    }

    return (
      <div
        role="presentation"
        onClick={toggleDrawer(false)}
        onKeyDown={toggleDrawer(false)}
      >
        <Divider />
        <List>
          {activatedIndices.map((listIndex, index) => {
            if (listIndex > -1) {
              let itemInfo = activatedItems[listIndex];
              return (
                <Link to={itemInfo.linkTo} key={index}>
                  <ListItem button key={itemInfo.label}>
                    <ListItemIcon>{itemInfo.icon}</ListItemIcon>
                    <ListItemText primary={itemInfo.label} />
                  </ListItem>
                </Link>
              );
            } else return <AmplifySignOut key="Sign Out" />;
          })}
        </List>
      </div>
    );
  };

  const getGridItems = () => {
    switch (departmentName) {
      case 'Accounts':
        return (
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={6} md={4}>
              <Link to="/invoice" className="hover-1">
                <Card
                  iconUri={InvoiceIcon}
                  label="Invoices"
                  backgroundColor="#2952CC"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/advanceAmount" className="hover-1">
                <Card
                  iconUri={AdvanceIcon}
                  label="Advance Payment"
                  backgroundColor="#142966"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/foodBills" className="hover-1">
                <Card
                  iconUri={FoodBillsIcon}
                  label="Food Bills"
                  backgroundColor="#0A1433"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/laundry" className="hover-1">
                <Card
                  iconUri={LaundryIcon}
                  label="Laundry"
                  backgroundColor="#0A1433"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/reports" className="hover-1">
                <Card
                  iconUri={ReportsIcon}
                  label="Reports"
                  backgroundColor="#142966"
                />
              </Link>
            </Grid>
            {posInvoiceFeatureEnabled ? (
              <Grid item xs={6} md={4}>
                <Link to="/posInvoice" className="hover-1">
                  <Card
                    iconUri={RestaurantBills}
                    label="Restaurant Invoice"
                    backgroundColor="#142966"
                  />
                </Link>
              </Grid>
            ) : (
              []
            )}
          </Grid>
        );
      case 'Reception':
        return (
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={6} md={4}>
              <Link to="/checkIn" className="hover-1">
                <Card
                  iconUri={CheckinIcon}
                  label="Check-In"
                  backgroundColor="#2952CC"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/checkOut" className="hover-1">
                <Card
                  iconUri={CheckoutIcon}
                  label="Check-Out"
                  backgroundColor="#142966"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/extraBed" className="hover-1">
                <Card
                  iconUri={ExtraBedIcon}
                  label="Extra Bed"
                  backgroundColor="#0A1433"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/guestRegister" className="hover-1">
                <Card
                  iconUri={GuestRegisterIcon}
                  label="Guest Register"
                  backgroundColor="#0A1433"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/advanceAmount" className="hover-1">
                <Card
                  iconUri={AdvanceIcon}
                  label="Advance Payment"
                  backgroundColor="#2952CC"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/invoice" className="hover-1">
                <Card
                  iconUri={InvoiceIcon}
                  label="Invoice"
                  backgroundColor="#142966"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/laundry" className="hover-1">
                <Card
                  iconUri={LaundryIcon}
                  label="Laundry"
                  backgroundColor="#142966"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/foodBills" className="hover-1">
                <Card
                  iconUri={FoodBillsIcon}
                  label="Food Bills"
                  backgroundColor="#0A1433"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/reports" className="hover-1">
                <Card
                  iconUri={ReportsIcon}
                  label="Reports"
                  backgroundColor="#2952CC"
                />
              </Link>
            </Grid>
            {/* advance bookings  */}
            <Grid item xs={6} md={4}>
              <Link to="/advanceBookings" className="hover-1">
                <Card
                  iconUri={AdvanceBookingsIcon}
                  label="Advance Bookings"
                  backgroundColor="#0A1433"
                />
              </Link>
            </Grid>
            {posInvoiceFeatureEnabled ? (
              <Grid item xs={6} md={4}>
                <Link to="/posInvoice" className="hover-1">
                  <Card
                    iconUri={RestaurantBills}
                    label="Restaurant Invoice"
                    backgroundColor="#142966"
                  />
                </Link>
              </Grid>
            ) : (
              []
            )}
          </Grid>
        );
      case 'House Keeping':
        return (
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={6} md={4}>
              <Link to="/extraBed" className="hover-1">
                <Card
                  iconUri={ExtraBedIcon}
                  label="Extra Bed"
                  backgroundColor="#2952CC"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/laundry" className="hover-1">
                <Card
                  iconUri={LaundryIcon}
                  label="Laundry"
                  backgroundColor="#142966"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/positionList" className="hover-1">
                <Card
                  iconUri={PositionListIcon}
                  label="Position List"
                  backgroundColor="#0A1433"
                />
              </Link>
            </Grid>
            <Grid item xs={6} md={4}>
              <Link to="/roomCleaners" className="hover-1">
                <Card
                  iconUri={RoomCleanerGridIcon}
                  label="Room Cleaner"
                  backgroundColor="#0A1433"
                />
              </Link>
            </Grid>
          </Grid>
        );
      default:
        return [];
    }
  };

  return (
    <div className={classes.root}>
      <React.Fragment key="left">
        <Button className={classes.drawerButton} onClick={toggleDrawer(true)}>
          <MenuIcon />
        </Button>
        <Drawer
          classes={{ paper: classes.dPaper }}
          anchor={'left'}
          open={sidebarOpen}
          style={{ inset: '156px 0px 0px 0px' }}
          onClose={toggleDrawer(false)}
        >
          {list('left')}
        </Drawer>
      </React.Fragment>

      {getGridItems()}
    </div>
  );
};

export default Home;
